import clsx from "clsx";
import { lazy, Suspense, useMemo } from "react";
import {
  additionalSavingsRate,
  neededCapital,
  overViewData,
  wishPensionWithAge,
} from "../../util/Calculation";
import { Currency } from "../../util/formatter";
import { OutputValues } from "../../util/types";
import styles from "./DataOverview.module.scss";

const PlotlyLazy = lazy(() => import("../plotly/Plotly"));

export type DataOverviewProps = {
  values?: OutputValues;
};

function DataOverview({ values }: DataOverviewProps) {
  const {
    preparedCapital: preparedCapitalValue,
    statutoryPension: statutoryPensionValue,
    private1: ruerupValue,
    private2: riesterValue,
    private3: privateValue,
    capital: capitalValue,
    rent: rentValue,
  } = useMemo<ReturnType<typeof overViewData>>(() => {
    if (!values)
      return {
        preparedCapital: 0,
        capital: 0,
        private1: 0,
        private2: 0,
        private3: 0,
        rent: 0,
        statutoryPension: 0,
      };
    return overViewData(values);
  }, [values]);

  const pensionStartValue = useMemo(() => {
    if (!values) return -1;
    return wishPensionWithAge({
      age_entry: values.age_entry,
      age: values.age,
      inflation: values.inflation,
      currentWishPension: values.wish_pension,
    });
  }, [values]);

  const neededCapitalValue = useMemo(() => {
    if (!values) return -1;
    return neededCapital({
      age_entry: values.age_entry,
      age_till: values.age_till,
      inflation: values.inflation,
      pension_inflation: pensionStartValue,
      performance_withdrawal: values.performance_withdrawal,
    });
  }, [pensionStartValue, values]);

  const missingCapitalValue = useMemo(() => {
    if (!values) return -1;
    return preparedCapitalValue - neededCapitalValue;
  }, [neededCapitalValue, preparedCapitalValue, values]);

  const additionalSavingsRateValue = useMemo(() => {
    if (!values) return -1;
    return additionalSavingsRate({
      performance_savings: values.performance_savings,
      age: values.age,
      age_entry: values.age_entry,
      missingCapital: missingCapitalValue,
    });
  }, [missingCapitalValue, values]);

  return (
    <>
      <div>
        <div className={styles.container}>
          <header className={styles.header}>Übersicht</header>
          <div className={styles.grid}>
            <div className={styles.gridLabel}>Wunschrente nach Inflation</div>
            <div className={clsx(styles.gridValue)}>
              <Currency value={pensionStartValue} />
            </div>
            <div className={styles.gridLabel}>
              Erforderliches Altersvermögen
            </div>
            <div
              className={clsx(
                styles.gridValue,
                styles.fw600,
                styles.finumBgYellow
              )}
            >
              <Currency value={neededCapitalValue} />
            </div>

            <div className={styles.gridLabel}>gesetzliche Rente</div>
            <div className={styles.gridValue}>
              <Currency value={statutoryPensionValue} />
            </div>
            <div className={styles.gridLabel}>Rüruprente (Schicht I)</div>
            <div className={styles.gridValue}>
              <Currency value={ruerupValue} />
            </div>
            <div className={styles.gridLabel}>
              Riester/betriebliche AV (Schicht II)
            </div>
            <div className={styles.gridValue}>
              <Currency value={riesterValue} />
            </div>
            <div className={styles.gridLabel}>Private Rente (Schicht III)</div>
            <div className={styles.gridValue}>
              <Currency value={privateValue} />
            </div>
            <div className={styles.gridLabel}>Mieteinnahmen</div>
            <div className={styles.gridValue}>
              <Currency value={rentValue} />
            </div>
            <div className={styles.gridLabel}>Geldanlagen</div>
            <div className={styles.gridValue}>
              <Currency value={capitalValue} />
            </div>

            <div className={styles.gridLabel}>Vorbereitetes Altersvermögen</div>
            <div
              className={clsx(
                styles.gridValue,
                styles.finumDarkBlue,
                styles.fw600
              )}
            >
              <Currency value={preparedCapitalValue} />
            </div>
            <div className={styles.gridLabel}>Lücke Altersvorsorgevermögen</div>
            <div
              className={clsx(
                styles.gridValue,
                styles.fw600,
                missingCapitalValue >= 0
                  ? styles.finumBgYellow
                  : styles.finumPurple
              )}
            >
              <Currency
                value={missingCapitalValue >= 0 ? 0 : -1 * missingCapitalValue}
              />
            </div>
            <div className={styles.gridLabel}>zusätzliche Sparrate</div>
            <div
              className={clsx(
                styles.gridValue,
                styles.fw600,
                additionalSavingsRateValue >= 0
                  ? styles.finumBgYellow
                  : styles.finumPurple
              )}
            >
              <Currency
                value={
                  additionalSavingsRateValue >= 0
                    ? 0
                    : -1 * additionalSavingsRateValue
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.chart}>
          <Suspense fallback={null}>
            <PlotlyLazy
              preparedCapital={preparedCapitalValue}
              statutoryPension={statutoryPensionValue}
              private1={ruerupValue}
              private2={riesterValue}
              private3={privateValue}
              capital={capitalValue}
              rent={rentValue}
              missingCapital={missingCapitalValue}
              neededCapital={neededCapitalValue}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
}

export default DataOverview;
